import { Caption, EllipsisText } from 'canoe';
import React from 'react';

import { ValueBox, ValueGroup, ValueSelectInput } from '@candu-ai/craft/ui';
import { BuiltInComponents } from 'craft/constants';
import { FlowActionButtons } from 'craft/resolvers/Flow';
import { useFlowAncestor } from 'craft/state';
import { FlowNextStep, FlowPreviousStep, FlowSetStep, HANDLER_TYPES } from 'craft/types';

import { InteractionFieldProps } from './interactionFields';

const handlerTypes = [
  {
    value: HANDLER_TYPES.FLOW_NEXT_STEP as const,
    label: 'Next Step',
  },
  {
    value: HANDLER_TYPES.FLOW_PREVIOUS_STEP as const,
    label: 'Previous Step',
  },
  {
    value: HANDLER_TYPES.FLOW_SET_STEP as const,
    label: 'Go to a specific Step',
  },
];

type Handler = {
  value: typeof handlerTypes[number]['value'];
  label: string;
};

type FlowAction = FlowNextStep | FlowPreviousStep | FlowSetStep;

export const FlowField = React.memo(
  ({ defaultValue, onChange, nodeIds }: InteractionFieldProps<FlowAction['handler']>) => {
    const flow = useFlowAncestor(nodeIds[0], [BuiltInComponents.Flow]);

    const defaultType = defaultValue?.type || HANDLER_TYPES.FLOW_NEXT_STEP;

    const setStepIdOptions = flow.stepIds
      .map((value, index) => ({
        label: `Step ${index + 1} ${flow.stepNames[index]}`,
        value,
      }))
      // you should not be able to link to the active step id...
      .filter(({ value }) => value !== flow.activeStepId);

    // if you're on step 0, don't link to it...
    const defaultStepIdForNewAction = flow.stepIds[flow.index === 0 ? 1 : 0];
    const onSetHandler = (selected: Handler | null) => {
      if (selected?.value === HANDLER_TYPES.FLOW_SET_STEP) {
        onChange({ type: HANDLER_TYPES.FLOW_SET_STEP, stepId: defaultStepIdForNewAction });
        return;
      }
      onChange({ type: selected?.value });
    };

    const onSetStepId = (selected: { value: string; label: string }) => {
      onChange({ type: defaultType, stepId: selected?.value });
    };

    const defaultStepId =
      (defaultValue as FlowSetStep['handler'])?.stepId || defaultStepIdForNewAction;

    return (
      <>
        <ValueGroup>
          <ValueBox isInGroup={{ isLast: defaultType !== HANDLER_TYPES.FLOW_SET_STEP }}>
            <ValueSelectInput<Handler>
              clearable={false}
              scale="small"
              items={handlerTypes}
              selectedItem={handlerTypes.find(({ value }) => value === defaultType)}
              onSelect={onSetHandler}
              calloutOffset="xxs"
            />
          </ValueBox>

          {defaultType === HANDLER_TYPES.FLOW_SET_STEP && (
            <ValueBox isInGroup={{ isLast: true }}>
              <ValueSelectInput
                scale="small"
                clearable={false}
                items={setStepIdOptions}
                selectedItem={setStepIdOptions.find(({ value }) => value === defaultStepId)}
                onSelect={onSetStepId}
                calloutOffset="xxs"
                renderValue={(valueProps) => (
                  <div css={{ display: 'flex' }}>
                    <EllipsisText
                      content={
                        <Caption color="White" mb="none">
                          {valueProps.selectedItem.label}
                        </Caption>
                      }
                      offset="s"
                      placement="top"
                    >
                      <span>{valueProps.selectedItem.label}</span>
                    </EllipsisText>
                  </div>
                )}
              />
            </ValueBox>
          )}
        </ValueGroup>
        {flow.flowNodeId && <FlowActionButtons spaceAfter nodeId={flow.flowNodeId} />}
      </>
    );
  },
);
